import { Link, useLocation } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { UserContext } from '../contexts/UserContext';
import { useAdmin } from '../contexts/AdminContext';
import { useContext, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { auth } from '../firebase';
import { useOffersAwaitingResponse } from '../hooks/useOffersAwaitingResponse';

// ~250-350px keyboard; the 150px threshold avoids false positives from small
// viewport nudges (URL bar, etc.). The visual viewport is measured in zoomed
// units, so its height is scaled back (height x scale) before comparing: zoom
// alone then changes nothing, and a keyboard still registers while zoomed
// (iOS Safari auto-zooms into small inputs and stays zoomed, which used to
// leave the bar floating over the keyboard). A stale measurement after resume
// can also shrink it, so a focused text field is required too: the bar can
// never stay hidden once nothing is being typed into.
// Full-screen conversation pages render without the tab bar (see the early
// return in BottomTabBar).
export function isChatRoute(pathname) {
  return /^\/(trade|purchase)-messages\//.test(pathname || '');
}

const TEXT_INPUT_TYPES = new Set(['', 'text', 'search', 'email', 'tel', 'url', 'password', 'number', 'date', 'datetime-local', 'month', 'time', 'week']);
export function isKeyboardLikelyOpen({ innerHeight, viewportHeight, viewportScale = 1, activeElement }) {
  const scale = viewportScale > 0 ? viewportScale : 1;
  if (!(innerHeight - viewportHeight * scale > 150)) return false;
  const el = activeElement;
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') return TEXT_INPUT_TYPES.has((el.getAttribute('type') || '').toLowerCase());
  return false;
}

const BottomTabBar = () => {
  const location = useLocation();
  const { unreadCount } = useNotifications();
  const { firestoreUser } = useContext(UserContext);
  const { isAdmin } = useAdmin();
  const currentUser = auth.currentUser;

  // Trading tab badge = open offers waiting on this user (owner, 2026-11-02),
  // the same number the top-nav Trading link and the hub's Offers tile show.
  // It used to be the unread trade + match notification count, which cleared
  // when the bell was read rather than when the offer was answered.
  const offersAwaitingResponse = useOffersAwaitingResponse();

  // Home tab badge: native-only. On the native app the top-nav bell is hidden on
  // every page except the dashboard, so unread notifications are surfaced here,
  // nudging the user back to Home to review them via the bell. Now that the
  // Trading tab counts offers rather than notifications, this covers EVERY
  // unread notification, trade and match ones included. On web the bell is
  // always present, so this stays hidden.
  const showHomeNotificationBadge =
    Capacitor.isNativePlatform() && unreadCount > 0;

  // Determine active tab based on current path
  const getActiveTab = () => {
    if (location.pathname === '/' || location.pathname === '/dashboard') return 'dashboard';
    if (location.pathname.startsWith('/latest-pedals')) return 'latest';
    // More (+) sheet destinations — gold "More" label while on these pages
    if (
      location.pathname.startsWith('/addpedal') ||
      location.pathname.startsWith('/recommendations') ||
      location.pathname.startsWith('/want-list-matches')
    ) return 'add';
    if (location.pathname.startsWith('/trading') ||
        location.pathname.startsWith('/matches') ||
        location.pathname.startsWith('/saved-matches') ||
        location.pathname.startsWith('/hidden-matches') ||
        location.pathname.startsWith('/trade-center') ||
        location.pathname.startsWith('/accepted-trades') ||
        location.pathname.startsWith('/completed-trades') ||
        location.pathname.startsWith('/accepted-purchases') ||
        location.pathname.startsWith('/completed-purchases')) return 'trading';
    if (location.pathname.startsWith('/my-pedals') || 
        location.pathname.startsWith('/pedalboard') ||
        location.pathname.startsWith('/wantlist')) return 'pedals';
    return '';
  };

  const activeTab = getActiveTab();

  // Hide the fixed bottom tab bar while the on-screen keyboard is open. The bar
  // is position:fixed bottom:0, so when iOS raises the keyboard the bar stays
  // pinned to the layout-viewport bottom and ends up floating ON TOP of the
  // keyboard (visible mid-screen). We detect the keyboard via the visualViewport
  // API — when the keyboard opens, the visual viewport shrinks well below the
  // layout viewport height — and hide the bar for that duration. Applies on
  // native and mobile web (Safari/Chrome); desktop rarely triggers the threshold.
  //
  // The bar once stayed hidden after the app sat in the background (2026-10):
  // the keyboard was dismissed while the WebView was suspended, no viewport
  // resize was delivered on resume, and keyboardOpen stayed latched until a
  // relaunch. So the measurement also re-runs on resume / visibility / window
  // resize / focus changes, and only counts as "keyboard" while a text field
  // actually has focus and the page isn't pinch-zoomed (zoom shrinks the
  // visual viewport the same way). See isKeyboardLikelyOpen.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const measure = () => {
      setKeyboardOpen(isKeyboardLikelyOpen({
        innerHeight: window.innerHeight,
        viewportHeight: vv.height,
        viewportScale: vv.scale,
        activeElement: document.activeElement,
      }));
    };
    // focusout fires before focus moves on; measure once the new
    // activeElement has settled.
    const measureSoon = () => setTimeout(measure, 0);
    vv.addEventListener('resize', measure);
    window.addEventListener('resize', measure);
    document.addEventListener('visibilitychange', measure);
    document.addEventListener('focusin', measure);
    document.addEventListener('focusout', measureSoon);
    let resumeHandle = null;
    let disposed = false;
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('resume', measure).then((h) => {
        if (disposed) h.remove(); else resumeHandle = h;
      }).catch(() => {});
    }
    measure();
    return () => {
      disposed = true;
      vv.removeEventListener('resize', measure);
      window.removeEventListener('resize', measure);
      document.removeEventListener('visibilitychange', measure);
      document.removeEventListener('focusin', measure);
      document.removeEventListener('focusout', measureSoon);
      if (resumeHandle) resumeHandle.remove();
    };
  }, []);
  // Two-state pattern lets us play a slide-down animation BEFORE unmounting:
  //   isAddSheetOpen   → controls mount (true while visible OR animating closed)
  //   isAddSheetClosing → toggled true to start the slide-down; unmount runs after.
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const [isAddSheetClosing, setIsAddSheetClosing] = useState(false);
  // Hold the pending close timeout so a rapid re-open can cancel it. Without this,
  // a rapid close→open→ pattern would let the original timeout fire and unmount
  // the sheet ~220ms after the second tap, even though the user reopened it.
  const closeTimeoutRef = useRef(null);
  const ADD_SHEET_ANIM_MS = 220;

  const cancelPendingClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const closeAddSheet = () => {
    if (!isAddSheetOpen || isAddSheetClosing) return;
    setIsAddSheetClosing(true);
    cancelPendingClose();
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      setIsAddSheetOpen(false);
      setIsAddSheetClosing(false);
    }, ADD_SHEET_ANIM_MS);
  };

  const openAddSheet = () => {
    cancelPendingClose();
    setIsAddSheetClosing(false);
    setIsAddSheetOpen(true);
    window.dispatchEvent(new CustomEvent('bottomTabBarAddOpened'));
  };

  const toggleAddSheet = () => {
    if (isAddSheetOpen && !isAddSheetClosing) closeAddSheet();
    else openAddSheet();
  };

  // Tear the sheet down with no animation — the user has committed to a
  // destination, so the sheet should just be gone.
  const dismissAddSheet = () => {
    cancelPendingClose();
    setIsAddSheetOpen(false);
    setIsAddSheetClosing(false);
  };

  // Route change → close immediately.
  useEffect(() => {
    dismissAddSheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Cancel any in-flight timeout if the component unmounts (defensive cleanup).
  useEffect(() => {
    return () => cancelPendingClose();
  }, []);

  const tabStyle = (isActive) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: '0.5rem 0',
    textDecoration: 'none',
    color: isActive ? '#C89F3C' : '#6B7280',
    transition: 'color 0.2s',
    position: 'relative'
  });

  /* NOTE: An earlier iteration of this component rendered a sliding "pill"
     behind the active tab. That design was reverted in favor of the current
     gold-text + dimmed-icon treatment (see svgStyle below). The supporting
     machinery — TAB_ORDER, activeIndex, haloVisible, tabBarRef, tabRefs, and
     the resize/measure effect that fed haloRect — was removed with this
     cleanup. If we ever bring the pill back, restore from git history rather
     than half-resurrecting these locals. */

  const svgStyle = (isActive) => ({
    width: '24px',
    height: '24px',
    marginBottom: '0.25rem',
    fill: isActive ? '#C89F3C' : '#6B7280',
    /* Icons in /images/Tab Icons/ are raster <img> tags whose native color is
       solid gold. When active we leave them untouched (they read as gold);
       when inactive we use a CSS filter to desaturate + dim them to match the
       neutral gray text. */
    filter: isActive ? 'none' : 'grayscale(1) brightness(0.9) opacity(0.75)',
    transition: 'filter 0.2s'
  });

  const labelStyle = (isActive) => ({
    fontSize: '0.75rem',
    fontWeight: '600',  // Always bold
    fontFamily: 'Nunito, sans-serif'
  });

  const badgeStyle = {
    position: 'absolute',
    top: '0.125rem',
    right: '50%',
    transform: 'translateX(1rem)',
    backgroundColor: '#EF4444',
    color: 'white',
    borderRadius: '9999px',
    padding: '0.0625rem 0.25rem',
    fontSize: '0.5rem',
    fontWeight: '700',
    minWidth: '1rem',
    height: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1.5px solid #F7F3EB'
  };

  // No tab bar inside a conversation, as in WhatsApp: the chat has its own
  // Back, and the bar's raised gold "+" (top: -1rem) sat on top of the
  // message field directly above it (owner, 2026-10-23).
  if (isChatRoute(location.pathname)) return null;

  return (
    <>
      <style>{`
        /* Hide tab bar on desktop (>1024px) */
        .bottom-tab-bar {
          display: none !important;
        }
        
        /* Show tab bar on mobile (<=1024px) */
        @media (max-width: 1024px) {
          .bottom-tab-bar {
            display: flex !important;
          }

          /* ...but hide it while the soft keyboard is open (see keyboardOpen).
             Two-class selector + !important beats the rule above. */
          .bottom-tab-bar.bottom-tab-bar--kb-hidden {
            display: none !important;
          }
          
          /* Add padding to body to account for tab bar + extra space for scrolling */
          body {
            padding-bottom: 150px !important;
          }
        }

        /* Defensive: some pages (e.g. PedalDetails.jsx) inject a global
           "* { box-sizing: border-box }" rule. That would shrink the gold "+"
           circle by its 4px border. Pin tab-bar elements to content-box so the
           circle/icons keep their intended dimensions on every page. */
        .bottom-tab-bar,
        .bottom-tab-bar * {
          box-sizing: content-box !important;
        }
      `}</style>
      <div 
        className={`bottom-tab-bar${keyboardOpen ? ' bottom-tab-bar--kb-hidden' : ''}`}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          height: '65px',
          /* zIndex 1002 keeps the tab bar (and its protruding "+" gold circle)
             above the Add sheet at zIndex 1001. Children inherit this stacking
             context — a child zIndex alone would be capped here. */
          zIndex: 1002,
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)',
          paddingBottom: 'env(safe-area-inset-bottom)'
        }}
      >
        {/* Dashboard Tab */}
        <Link to="/dashboard" style={tabStyle(activeTab === 'dashboard')} onClick={() => window.scrollTo(0, 0)}>
          <img src="/images/Tab Icons/Home.svg" alt="Home" style={svgStyle(activeTab === 'dashboard')} />
          {showHomeNotificationBadge && (
            <span style={badgeStyle}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          <span style={labelStyle(activeTab === 'dashboard')}>Home</span>
        </Link>

        {/* Browse Pedals Tab */}
        <Link to="/latest-pedals" state={{ fresh: true }} data-tip-anchor="browse" style={tabStyle(activeTab === 'latest')} onClick={() => window.scrollTo(0, 0)}>
          <img src="/images/Tab Icons/Pedals.svg" alt="Browse" style={svgStyle(activeTab === 'latest')} />
          <span style={labelStyle(activeTab === 'latest')}>Browse</span>
        </Link>

        {/* Add Tab - Center with special styling. Toggles the bottom sheet for logged-in users;
            logged-out users go straight to signup. The tab bar parent at zIndex 1002 keeps the
            protruding "+" gold circle above the sheet (zIndex 1001) so it stays visible and
            remains a tappable close target. */}
        <button
          type="button"
          onClick={() => {
            if (!currentUser) {
              window.location.href = '/signup';
              return;
            }
            toggleAddSheet();
          }}
          style={{
            ...tabStyle(activeTab === 'add'),
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <div 
            style={{
              position: 'absolute',
              top: '-1rem',
              width: '3rem',
              height: '3rem',
              borderRadius: '50%',
              backgroundColor: '#C89F3C',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: '4px solid #F7F3EB'
            }}
          >
            <img src="/images/Tab Icons/Add.svg" alt="Add" style={{ width: '24px', height: '24px', filter: 'brightness(0) invert(1)' }} />
          </div>
          <span style={{ ...labelStyle(activeTab === 'add'), marginTop: '2.25rem' }}>More</span>
        </button>

        {/* My Pedals Tab */}
        <Link to="/my-pedals/pedalboard" style={tabStyle(activeTab === 'pedals')} onClick={() => window.scrollTo(0, 0)}>
          <img src="/images/Tab Icons/My.svg" alt="My Pedals" style={svgStyle(activeTab === 'pedals')} />
          <span style={labelStyle(activeTab === 'pedals')}>My Pedals</span>
        </Link>

        {/* Trading Tab */}
        <Link to="/trading" style={tabStyle(activeTab === 'trading')} onClick={() => window.scrollTo(0, 0)}>
          <img src="/images/Tab Icons/Trades.svg" alt="Trading" style={svgStyle(activeTab === 'trading')} />
          {offersAwaitingResponse > 0 && (
            <span style={badgeStyle}>
              {offersAwaitingResponse > 9 ? '9+' : offersAwaitingResponse}
            </span>
          )}
          <span style={labelStyle(activeTab === 'trading')}>Trading</span>
        </Link>
      </div>

      {/* Bottom-sheet modal — Add menu (mobile only; sits ABOVE the tab bar so the tab bar stays visible).
          Animation: slides up on open, slides DOWN on close (driven by isAddSheetClosing flag). */}
      {isAddSheetOpen && (
        <>
          <style>{`
            @keyframes addSheetFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes addSheetFadeOut { from { opacity: 1; } to { opacity: 0; } }
            @keyframes addSheetSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
            @keyframes addSheetSlideDown { from { transform: translateY(0); } to { transform: translateY(100%); } }
          `}</style>
          {/* Backdrop: covers the screen ABOVE the tab bar only. Tab bar at zIndex 1002 stays visible and tappable. */}
          <div
            onClick={closeAddSheet}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 'calc(65px + env(safe-area-inset-bottom))',
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              zIndex: 999,
              animation: isAddSheetClosing
                ? `addSheetFadeOut ${ADD_SHEET_ANIM_MS}ms ease-in forwards`
                : 'addSheetFadeIn 0.18s ease-out'
            }}
          />
          {/* Sheet: anchors to the top of the tab bar and slides up from there.
              No box-shadow — keeps the seam between the sheet and the tab bar clean
              (both are pure white) so they read as one continuous surface. */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Add menu"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 'calc(65px + env(safe-area-inset-bottom))',
              backgroundColor: 'white',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              zIndex: 1001,
              paddingTop: '4px',
              paddingBottom: '12px',
              animation: isAddSheetClosing
                ? `addSheetSlideDown ${ADD_SHEET_ANIM_MS}ms ease-in forwards`
                : `addSheetSlideUp ${ADD_SHEET_ANIM_MS}ms ease-out`
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px', paddingBottom: '6px' }}>
              <div style={{ width: '40px', height: '4px', borderRadius: '2px', backgroundColor: '#D1D5DB' }} />
            </div>

            {(() => {
              // Build menu items. The Admin entry is appended only for admins
              // and reuses the same row chrome (padding, font, borders, divider
              // logic). For non-admins the array is exactly what it was before
              // this change — same length, same rows, identical sheet height
              // and seams. The map's `arr.length - 1` divider rule still draws
              // the correct internal dividers because it operates on the
              // already-filtered array.
              const menuItems = [
                { label: 'Add Pedal', to: '/addpedal', state: { tab: 'pedal' } },
                { label: 'Add Want', to: '/addpedal', state: { tab: 'want' } },
                { label: 'Trade Opportunities', to: '/recommendations', state: undefined },
                { label: 'Saved Pedals', to: '/want-list-matches', state: undefined }
              ];
              if (isAdmin) {
                menuItems.push({ label: 'Admin', to: '/admin', state: undefined, color: '#dc2626' });
              }
              return menuItems.map((item, idx, arr) => (
                <Link
                  key={item.label}
                  to={item.to}
                  state={item.state}
                  onClick={() => {
                    // Close here rather than leaning on the route-change effect.
                    // Tapping the entry for the page you're already on navigates
                    // to the same pathname, so that effect never fires and the
                    // sheet just sat there looking broken. Closing directly is
                    // correct either way — for a real navigation the effect below
                    // is then simply a no-op.
                    dismissAddSheet();
                    window.scrollTo(0, 0);
                  }}
                  style={{
                    display: 'block',
                    padding: '16px 20px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: item.color || '#5A9ECB',
                    textDecoration: 'none',
                    fontFamily: 'Nunito, sans-serif',
                    borderTop: idx === 0 ? '1px solid #F3F4F6' : 'none',
                    borderBottom: idx < arr.length - 1 ? '1px solid #F3F4F6' : 'none'
                  }}
                >
                  {item.label}
                </Link>
              ));
            })()}
          </div>
        </>
      )}
    </>
  );
};

export default BottomTabBar;

